import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { ActivityIcon, EditIcon, ProfileIcon, SourceIcon } from "@icons";
import { WORKSPACE_ACTIVITY_OUTCOMES } from "@components/activityTimeline/activityTimelineOutcomes.lib";
import { ActivityFilterPanel } from "@components/activityTimeline/filters/ActivityFilterPanel";
import {
    ACTIVITY_FILTER_TAB_KEYS,
    type ActivityFilterTab,
} from "@components/activityTimeline/filters/activityFilters.types";
import {
    HistoryChangeFilters,
    HistoryFilters,
} from "@hooks/view/useHistoryPageFiltersCache";
import { WORKSPACE_PICKABLE_SERIES } from "../components/actorSeriesFilterItems";
import { useActionFilterRows } from "../components/ActivityFilterCombobox";
import { useActorFilterRows } from "../components/ActorFilterCombobox";
import { useChannelFilterRows } from "../components/ChannelFilterCombobox";
import type { HistoryObjectFieldOption } from "../components/historyObjectFieldFilterOptions";
import { useObjectFieldFilterRows } from "../components/ObjectFieldFilterCombobox";

interface HistoryFilterTabsParams {
    filters: HistoryFilters;
    onChange: (filters: Partial<HistoryFilters>) => void;
    isChannelFilterEnabled: boolean;
}

type HistoryFilterRowsParams = Omit<
    HistoryFilterTabsParams,
    "isChannelFilterEnabled"
>;

interface HistoryChangeFilterTabsParams {
    filters: HistoryChangeFilters;
    onChange: (filters: HistoryChangeFilters) => void;
    isChannelFilterEnabled: boolean;
    /** Listed above the Activity tab's objects. */
    outcomes: readonly ActivityOutcomeKind[];
    /** The Activity tab's objects; workspace History's by default. */
    objectOptions?: readonly HistoryObjectFieldOption[];
}

const UPDATED_BY_PARAMS = { pickableSeries: WORKSPACE_PICKABLE_SERIES };

/** Owns its search, so the rows list in full each time the tab opens. */
const UpdatedByPanel = (params: HistoryFilterRowsParams) => (
    <ActivityFilterPanel
        rows={useActorFilterRows({ ...params, ...UPDATED_BY_PARAMS })}
        emptyLabel="No matching people"
    />
);

/** Updated through, Activity and Action, which the workspace and firm Activity pages share. */
export function useHistoryChangeFilterTabs({
    filters,
    onChange,
    isChannelFilterEnabled,
    outcomes,
    objectOptions,
}: HistoryChangeFilterTabsParams): ActivityFilterTab[] {
    const params = { filters, onChange };
    const channelRows = useChannelFilterRows(params);
    const activityRows = useObjectFieldFilterRows({
        ...params,
        outcomes,
        options: objectOptions,
    });
    const actionRows = useActionFilterRows(params);

    return [
        ...(isChannelFilterEnabled
            ? [
                  {
                      key: ACTIVITY_FILTER_TAB_KEYS.UPDATED_THROUGH,
                      label: "Updated through",
                      icon: <SourceIcon />,
                      chipLabel: channelRows.chipLabel,
                      onClear: channelRows.onClear,
                      panel: (
                          <ActivityFilterPanel
                              rows={channelRows}
                              emptyLabel="No matching channels"
                          />
                      ),
                  },
              ]
            : []),
        {
            key: ACTIVITY_FILTER_TAB_KEYS.ACTIVITY,
            label: "Activity",
            icon: <ActivityIcon />,
            chipLabel: activityRows.chipLabel,
            onClear: activityRows.onClear,
            panel: (
                <ActivityFilterPanel
                    rows={activityRows}
                    emptyLabel="No matching activity"
                />
            ),
        },
        {
            key: ACTIVITY_FILTER_TAB_KEYS.ACTION,
            label: "Action",
            icon: <EditIcon />,
            chipLabel: actionRows.chipLabel,
            onClear: actionRows.onClear,
            panel: (
                <ActivityFilterPanel
                    rows={actionRows}
                    emptyLabel="No matching actions"
                />
            ),
        },
    ];
}

/** The workspace Activity page's Filter popover tabs. */
export function useHistoryFilterTabs({
    filters,
    onChange,
    isChannelFilterEnabled,
}: HistoryFilterTabsParams): ActivityFilterTab[] {
    const params = { filters, onChange };
    const actorRows = useActorFilterRows({ ...params, ...UPDATED_BY_PARAMS });
    const changeTabs = useHistoryChangeFilterTabs({
        ...params,
        isChannelFilterEnabled,
        outcomes: WORKSPACE_ACTIVITY_OUTCOMES,
    });

    return [
        {
            key: ACTIVITY_FILTER_TAB_KEYS.UPDATED_BY,
            label: "Updated by",
            icon: <ProfileIcon />,
            chipLabel: actorRows.chipLabel,
            onClear: actorRows.onClear,
            panel: <UpdatedByPanel {...params} />,
        },
        ...changeTabs,
    ];
}
